import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, Heart } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const OAR_LOGO = "https://customer-assets.emergentagent.com/job_a27b2f16-dbd8-4903-b7cf-e0c3f4166774/artifacts/zuafya4h_LOGO%20OAR%20jpeg.jpg";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/about', label: t('nav.about') },
    { href: '/network', label: t('nav.network') },
    { href: '/programs', label: t('nav.programs') },
    { href: '/partners', label: t('nav.partners') },
    { href: '/contact', label: t('nav.contact') },
  ];

  const ctaLinks = [
    { href: '/apply', label: t('nav.apply'), variant: 'secondary' },
    { href: '/membership', label: t('nav.membership'), variant: 'primary' },
    { href: '/donate', label: t('nav.donate'), variant: 'donate' },
  ];

  const languageOptions = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  ];

  const currentLang = languageOptions.find(l => l.code === language);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-md'
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      {/* Tricolor top line */}
      <div className="tricolor-line" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4" data-testid="logo-link">
            <img
              src={OAR_LOGO}
              alt="OAR Logo"
              className="h-12 w-12 object-contain rounded-sm"
            />
            <div className="hidden sm:block border-l border-oar-border pl-4">
              <span className="text-oar-blue font-heading font-semibold text-lg">OAR</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
                className={`text-sm uppercase tracking-widest transition-colors duration-300 ${
                  location.pathname === link.href
                    ? 'text-oar-blue font-semibold'
                    : 'text-oar-text-secondary hover:text-oar-blue'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Selector & CTA Buttons & Mobile Menu Button */}
          <div className="flex items-center gap-3">
            {/* CTA Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-2">
              <Link
                to="/apply"
                data-testid="nav-apply"
                className="text-xs uppercase tracking-wider px-4 py-2 text-oar-green border border-oar-green hover:bg-oar-green hover:text-white rounded-sm transition-all"
              >
                {t('nav.apply')}
              </Link>
              <Link
                to="/membership"
                data-testid="nav-membership"
                className="text-xs uppercase tracking-wider px-4 py-2 bg-oar-red hover:bg-oar-red-dark text-white rounded-sm transition-all"
              >
                {t('nav.membership')}
              </Link>
              <Link
                to="/donate"
                data-testid="nav-donate"
                className="flex items-center gap-1 text-xs uppercase tracking-wider px-4 py-2 bg-oar-orange hover:bg-oar-orange/90 text-white rounded-sm transition-all"
              >
                <Heart className="w-3 h-3" />
                {t('nav.donate')}
              </Link>
            </div>

            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="language-selector"
                className="flex items-center gap-2 px-3 py-2 text-sm text-oar-text-secondary hover:text-oar-blue transition-colors rounded-sm border border-oar-border hover:border-oar-blue/50"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{currentLang?.label}</span>
                <span className="sm:hidden">{currentLang?.code.toUpperCase()}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-white border border-oar-border shadow-lg"
              >
                {languageOptions.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`lang-option-${lang.code}`}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${
                      language === lang.code
                        ? 'text-oar-blue bg-oar-blue-soft font-medium'
                        : 'text-oar-text-secondary hover:text-oar-blue hover:bg-oar-blue-soft/50'
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-oar-text-secondary hover:text-oar-blue transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden py-6 border-t border-oar-border bg-white"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-link-${link.href.replace('/', '') || 'home'}`}
                  onClick={() => setIsOpen(false)}
                  className={`text-sm uppercase tracking-widest transition-colors py-2 ${
                    location.pathname === link.href
                      ? 'text-oar-blue font-semibold'
                      : 'text-oar-text-secondary hover:text-oar-blue'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {/* Mobile CTA Buttons */}
              <div className="pt-4 mt-4 border-t border-oar-border flex flex-col gap-3">
                <Link
                  to="/apply"
                  onClick={() => setIsOpen(false)}
                  className="text-center text-sm uppercase tracking-wider px-4 py-3 text-oar-green border border-oar-green rounded-sm"
                >
                  {t('nav.apply')}
                </Link>
                <Link
                  to="/membership"
                  onClick={() => setIsOpen(false)}
                  className="text-center text-sm uppercase tracking-wider px-4 py-3 bg-oar-red text-white rounded-sm"
                >
                  {t('nav.membership')}
                </Link>
                <Link
                  to="/donate"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center gap-2 text-sm uppercase tracking-wider px-4 py-3 bg-oar-orange text-white rounded-sm"
                >
                  <Heart className="w-4 h-4" />
                  {t('nav.donate')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
